// Banner — full-width inline message block (error / warn / info).
//
// Replaces the hand-rolled banners scattered across pages, several of which
// used dark-only colors (e.g. `text-red-300`) that were illegible in light
// mode. Colors follow the StatusBadge adaptive convention:
//   bg-<c>-500/15  text-<c>-700 dark:text-<c>-400  border-<c>-500/30
//
// Usage:
//   <Banner variant="error">Failed to load data. {detail}</Banner>
//   <Banner variant="warn" onDismiss={() => …}>Heads up…</Banner>

import type { ReactNode } from "react";
import { AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import { cn } from "../../lib/utils";

export type BannerVariant = "error" | "warn" | "info";

interface BannerProps {
  variant?: BannerVariant;
  children: ReactNode;
  /** Optional title rendered bold above the body. */
  title?: string;
  /** When provided, renders a dismiss button. */
  onDismiss?: () => void;
  className?: string;
}

const VARIANT_CLASSES: Record<BannerVariant, string> = {
  error: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
  warn: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  info: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30",
};

const VARIANT_ICON: Record<BannerVariant, typeof Info> = {
  error: AlertCircle,
  warn: AlertTriangle,
  info: Info,
};

export default function Banner({
  variant = "info",
  children,
  title,
  onDismiss,
  className,
}: BannerProps) {
  const Icon = VARIANT_ICON[variant];
  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={cn(
        "flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm",
        VARIANT_CLASSES[variant],
        className,
      )}
    >
      <Icon size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        <div className={title ? "mt-0.5" : undefined}>{children}</div>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-mr-1 -mt-0.5 shrink-0 rounded p-0.5 opacity-70 hover:opacity-100 transition-opacity"
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
